/***
 *
 *   TOOLTIP
 *   Ported from ui-reference/src/components/ui/tooltip.tsx, which wraps Radix.
 *   We are not adding Radix (see docs/ui-refresh/PLAN.md), so the behaviour is
 *   hand-rolled:
 *
 *     - opens on pointer enter AND on keyboard focus, so it is reachable
 *       without a mouse
 *     - closes on leave, blur and Escape
 *     - `delayDuration` before opening, matching Radix's default of 700ms
 *     - linked to the trigger with aria-describedby, and marked
 *       role="tooltip", so screen readers announce it
 *
 *   What we lose versus Radix: collision-aware flipping. `side` is honoured as
 *   given, so pick one that fits — the collapsed sidebar uses side="right".
 *
 *   PROPS
 *   label: tooltip text
 *   side: top/bottom/left/right (default: top)
 *   delayDuration: ms before opening (default: 700, Radix's default)
 *   children: the trigger
 *
 **********/

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { cn } from 'components/lib';
import Style from './tooltip.tailwind.js';

export function Tooltip(props) {
    const { label, side = 'top', delayDuration = 700, disabled } = props;
    const [open, setOpen] = useState(false);
    const timer = useRef(null);
    const id = useId();

    const clear = useCallback(() => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);

    const show = useCallback(
        (immediate) => {
            if (disabled || !label) return;
            clear();
            if (immediate || !delayDuration) return setOpen(true);
            timer.current = setTimeout(() => setOpen(true), delayDuration);
        },
        [clear, delayDuration, disabled, label]
    );

    const hide = useCallback(() => {
        clear();
        setOpen(false);
    }, [clear]);

    // Escape closes it even while the pointer is still over the trigger.
    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e) => e.key === 'Escape' && hide();
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, hide]);

    useEffect(() => clear, [clear]);

    if (!label) return props.children;

    return (
        <span
            className={cn(Style.wrapper, props.className)}
            onPointerEnter={() => show()}
            onPointerLeave={hide}
            // Focus arrives without delay: a keyboard user has already
            // committed to the element, so waiting only hides information.
            onFocus={() => show(true)}
            onBlur={hide}
            aria-describedby={open ? id : undefined}>
            {props.children}

            {open && (
                <span role="tooltip" id={id} className={cn(Style.content, Style[side])}>
                    {label}
                </span>
            )}
        </span>
    );
}
